import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ProfileCard from "../../components/profile/ProfileCard";
import ProfileActions from "../../components/profile/ProfileActions";
import ProfileStats from "../../components/profile/ProfileStats";
import FollowersModal from "../../components/profile/FollowersModal";
import FollowingModal from "../../components/profile/FollowingModal";
import Loader from "../../components/Loader";
import Message from "../../components/Message";

import { ProfileRealtyObjects } from "../../features/realtyList";

import { logout, updateUserProfileInfo } from "../../actions/userActions";
import usePresenceSubscription from "../../features/presence/hooks/usePresenceSubscription";
import { presenceToProfileProps } from "../../features/presence/lib/formatPresence";
import { selectPresenceByUserId } from "../../features/presence/model/presenceSelectors";
import { selectIdentity } from "../../selectors/userSelectors";

const FALLBACK_AVATAR = "images/no-profile-picture.png";
const CLIPBOARD_FAIL_TOAST = "Не вдалося скопіювати посилання.";

const buildSocialLinks = (userInfo) =>
  [
    userInfo.facebook && {
      network: "facebook",
      url: userInfo.facebook,
      label: "Facebook",
      icon: "fa-facebook-f",
    },
    userInfo.instagram && {
      network: "instagram",
      url: userInfo.instagram,
      label: "Instagram",
      icon: "fa-instagram",
    },
    userInfo.youtube && {
      network: "youtube",
      url: userInfo.youtube,
      label: "YouTube",
      icon: "fa-youtube",
    },
  ].filter(Boolean);

const mapPeer = (peer) => ({
  id: peer.public_id || peer.id,
  name: peer.fullname,
  handle: peer.handle ? "@" + peer.handle : "",
  avatar: peer.user_image || FALLBACK_AVATAR,
  href: `/user/${peer.handle || peer.public_id}`,
});

const buildProfileData = (userInfo, presenceProps) => ({
  user: {
    id: userInfo.public_id,
    name: userInfo.fullname || "",
    avatar: userInfo.user_image || FALLBACK_AVATAR,
    avatarAlt: `Аватар ${userInfo.fullname || ""}`.trim(),
    bio: userInfo.description || "",
    isOnline: presenceProps.isOnline,
    lastSeen: presenceProps.lastSeen,
    contacts: {
      phone: userInfo.phone_number || "",
      email: userInfo.email || "",
    },
  },
  company: userInfo.company || null,
  socialLinks: buildSocialLinks(userInfo),
  stats: {
    realtyCount: userInfo.realty_count || 0,
    followers:
      userInfo.followers_count ?? (userInfo.followers?.length || 0),
    following:
      userInfo.following_count ?? (userInfo.following?.length || 0),
  },
  followers: (userInfo.followers || []).map(mapPeer),
  following: (userInfo.following || []).map(mapPeer),
});

function ProfileScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isFollowersOpen, setFollowersOpen] = useState(false);
  const [isFollowingOpen, setFollowingOpen] = useState(false);
  // R6.4 Seam C — surface-local share label-swap state. Mirrors the
  // /feed /search /saved per-card pattern. NO shared clipboard hook
  // (SR-6 local duplication preferred over abstraction).
  const [shareCopied, setShareCopied] = useState(false);
  // R8.3.2 — timer cleanup containment. Ref-tracked so the unmount
  // effect can clear an in-flight reset and a rapid re-copy can
  // clear the previous reset before starting a new one.
  const shareCopyTimerRef = useRef(null);

  const { userInfo, loading, error } = useSelector((state) => state.userLogin);
  const identity = useSelector(selectIdentity);
  const ownUserId = identity?.userId ?? null;
  const ownPresence = useSelector((state) =>
    ownUserId != null ? selectPresenceByUserId(state, ownUserId) : null
  );
  const presenceSubscribeIds = useMemo(
    () => (userInfo && ownUserId != null ? [ownUserId] : []),
    [userInfo, ownUserId],
  );
  usePresenceSubscription(presenceSubscribeIds);

  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
      return;
    }
    dispatch(updateUserProfileInfo());
  }, [dispatch, navigate, userInfo?.public_id]);

  const profile = useMemo(() => {
    if (!userInfo) return null;
    // Authoritative Redis/WS presence — never hardcode self=online from REST.
    return buildProfileData(userInfo, presenceToProfileProps(ownPresence));
  }, [userInfo, ownPresence]);

  const handleLogout = () => dispatch(logout());

  // R6.4 Seam C — local clipboard copy of the canonical public
  // profile URL. NO share token, NO referral, NO analytics.
  // Clipboard failures surface a safe Ukrainian toast (B1-18).
  const handleShareCopy = async () => {
    if (!userInfo) return;
    const handle = userInfo.handle || userInfo.public_id;
    if (!handle) return;
    const url = `${window.location.origin}/user/${handle}`;
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== "function"
      ) {
        toast.error(CLIPBOARD_FAIL_TOAST);
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      if (shareCopyTimerRef.current) {
        window.clearTimeout(shareCopyTimerRef.current);
      }
      shareCopyTimerRef.current = window.setTimeout(() => {
        setShareCopied(false);
      }, 2000);
    } catch (_err) {
      toast.error(CLIPBOARD_FAIL_TOAST);
    }
  };

  // R8.3.2 — clear pending share-copy reset on unmount.
  useEffect(() => {
    return () => {
      if (shareCopyTimerRef.current) {
        window.clearTimeout(shareCopyTimerRef.current);
      }
    };
  }, []);

  const ownerMenuItems = [
    {
      id: 'share',
      label: shareCopied ? 'Посилання скопійовано' : 'Поширити',
      icon: 'fa-share-nodes',
      kind: 'button',
      onClick: handleShareCopy,
    },
    { id: 'change-password', label: 'Змінити пароль', icon: 'fa-key', kind: 'link',   href: '/profile/change-password' },
    { id: 'logout',          label: 'Вийти',          icon: 'fa-arrow-right-from-bracket', kind: 'button', onClick: handleLogout, danger: true },
  ];

  // Redirect-only: effect navigates to /login; no user-visible MainLayout UI.
  if (!userInfo) return null;

  // Visible loading state must participate in the #Csection grid contract.
  // With current derivation profile is built from userInfo, so this branch is
  // defensive; keep the shell so any future loading edge stays compliant.
  if (loading && !profile) {
    return (
      <section className="profile-section" id="Csection">
        <Loader />
      </section>
    );
  }

  // Defensive null: profile is derived from userInfo above; no visible UI.
  if (!profile) return null;

  // R6.4 Seam B — `stats` (statistics.html) removed (no React route).
  // Billing restored as `/profile/billing` (PAYMENT-B1).
  const ownerSecondaryActions = [
    { id: 'edit',    label: 'Редагувати',  icon: 'fa-pen-to-square', href: '/profile/edit' },
    { id: 'billing', label: 'Оплата',      icon: 'fa-credit-card',   href: '/profile/billing' },
    { id: 'saved',   label: 'Збережені',   icon: 'fa-bookmark',      href: '/saved' },
    { id: 'phone',  label: 'Телефон',     icon: 'fa-phone',         href: profile.user.contacts.phone ? 'tel:' + profile.user.contacts.phone : '#' },
    { id: 'email',  label: 'Емейл',       icon: 'fa-envelope',      href: profile.user.contacts.email ? 'mailto:' + profile.user.contacts.email : '#' },
  ];

  return (
    <section className="profile-section" id="Csection">
      {error && <Message variant="danger">{error}</Message>}

      <section
        className="profile-info-section"
        aria-label="Інформація про профіль"
      >
        <ProfileCard
          user={profile.user}
          company={profile.company}
          socialLinks={profile.socialLinks}
          menuItems={ownerMenuItems}
        />
        <ProfileActions secondaryActions={ownerSecondaryActions} />
        <ProfileStats
          stats={profile.stats}
          onOpenFollowers={() => setFollowersOpen(true)}
          onOpenFollowing={() => setFollowingOpen(true)}
        />
      </section>

      <section className="profile-objects" id="profile-objects">
        <ProfileRealtyObjects />
      </section>

      <FollowersModal
        users={profile.followers}
        isOpen={isFollowersOpen}
        onClose={() => setFollowersOpen(false)}
      />
      <FollowingModal
        users={profile.following}
        isOpen={isFollowingOpen}
        onClose={() => setFollowingOpen(false)}
      />
    </section>
  );
}

export default ProfileScreen;
